'use client';

import React from 'react';
import { Clock, ChevronRight } from 'lucide-react';
import { usePlayback } from '@/lib/playbackEngine';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatHourRange(startHour: number, durationHours: number): string {
  const fmt = (h: number) => {
    if (h === 0 || h === 24) return '12:00 AM';
    if (h < 12) return `${h}:00 AM`;
    if (h === 12) return '12:00 PM';
    return `${h - 12}:00 PM`;
  };
  return `${fmt(startHour)}–${fmt(startHour + durationHours)}`;
}

export default function PublicProgramGuide() {
  const { schedule, activeBlock, library } = usePlayback();

  const now = new Date();
  const today = now.getDay();
  const currentDecimalHour = now.getHours() + now.getMinutes() / 60;

  // Show today's blocks + next-day blocks if today is empty
  const todayBlocks = schedule
    .filter((b) => b.day === today)
    .sort((a, b) => a.startHour - b.startHour);

  const displayBlocks = todayBlocks.length > 0
    ? todayBlocks
    : schedule
        .filter((b) => b.day === (today + 1) % 7)
        .sort((a, b) => a.startHour - b.startHour);

  const dayLabel = todayBlocks.length > 0
    ? 'Today on Rumbaexplosions'
    : `Tomorrow on Rumbaexplosions (${DAY_NAMES[(today + 1) % 7]})`;

  if (displayBlocks.length === 0) {
    return (
      <section className="px-6 md:px-12 py-12 bg-card/50 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <p className="section-label mb-1">Program Guide</p>
          <h2 className="text-xl font-bold text-foreground mb-3">Today on Rumbaexplosions</h2>
          <p className="text-sm text-muted-foreground">
            No shows scheduled yet. The admin can build the program schedule in the Schedule Builder.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 md:px-12 py-12 bg-card/50 border-t border-border">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="section-label mb-1">Program Guide</p>
            <h2 className="text-xl font-bold text-foreground">{dayLabel}</h2>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock size={12} />
            <span>All times local</span>
          </div>
        </div>

        <div className="space-y-2">
          {displayBlocks.map((block) => {
            const isLive = activeBlock?.id === block.id;
            const isDone =
              todayBlocks.length > 0 &&
              block.startHour + block.durationHours <= currentDecimalHour;
            const isUpcoming = !isLive && !isDone;

            const songs = block.songIds
              .map((id) => library.find((s) => s.id === id))
              .filter(Boolean);

            return (
              <div
                key={block.id}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl border transition-all duration-150 ${
                  isLive
                    ? 'bg-primary/10 border-primary/30'
                    : isDone
                    ? 'bg-muted/30 border-border opacity-50' :'bg-card border-border hover:border-accent/30 hover:bg-accent/5'
                }`}
              >
                {/* Time */}
                <div className="w-36 flex-shrink-0">
                  <span className={`text-xs font-mono font-medium tabular-nums ${isLive ? 'text-primary' : 'text-muted-foreground'}`}>
                    {formatHourRange(block.startHour, block.durationHours)}
                  </span>
                </div>

                {/* Show info */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isDone ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {block.showName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {block.description
                      ? block.description
                      : songs.length > 0
                      ? `${songs.length} track${songs.length !== 1 ? 's' : ''} · Auto DJ`
                      : 'Auto DJ'}
                  </p>
                </div>

                {/* Status badge */}
                {isLive && (
                  <span className="flex items-center gap-1 text-xs font-bold text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded-full flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-success live-pulse" />
                    ON AIR
                  </span>
                )}
                {isUpcoming && (
                  <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}